/* mapping.js — backend ⇄ UI shape adapters for the M0 partner pages.
   Backend parent / courier / timeline JSON → the field names the existing
   components render (p.legal, p.gst, c.code, poc.m1 …) and back. */
import {POC_ROLE_LABEL, POC_ROLE_ENUM, INTEGRATION_LABEL} from '../supplyMeta'

/* deterministic avatar tint from the partner name (replaces dummy p.color) */
const PALETTE = ['#1976D2', '#7B1FA2', '#00796B', '#C2185B', '#5D4037', '#455A64', '#E64A19', '#303F9F']
export const colorFor = (name) => {
  let h = 0
  const s = String(name || '')
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997
  return PALETTE[h % PALETTE.length]
}

const STATUS_LABEL = {ACTIVE: 'Active', INACTIVE: 'Inactive', DRAFT: 'Draft'}

/* backend contact → UI poc */
export const mapContact = (c) => ({
  contactId: c.contactId != null ? c.contactId : null,
  name: c.name || '',
  desig: c.designation || '',
  email: c.email || '',
  m1: c.mobilePrimary || '',
  m2: c.mobileSecondary || '',
  linkedin: c.linkedinUrl || '',
  notes: c.notes || '',
  roles: (c.roles || []).map((r) => POC_ROLE_LABEL[r] || r),
  level: c.level,
  status: c.isActive === false ? 'Inactive' : 'Active',
})

/* UI poc → backend contact (role labels → enums; contactId null for new) */
export const toBackendContact = (poc) => ({
  contactId: poc.contactId != null ? poc.contactId : null,
  name: (poc.name || '').trim(),
  designation: poc.desig || null,
  email: (poc.email || '').trim(),
  mobilePrimary: String(poc.m1 || '').replace(/\D/g, '').slice(-10),
  mobileSecondary: String(poc.m2 || '').replace(/\D/g, '').slice(-10),
  linkedinUrl: poc.linkedin || null,
  notes: poc.notes || null,
  roles: (poc.roles || []).map((r) => POC_ROLE_ENUM[r] || r),
  isActive: poc.status !== 'Inactive',
})

const codFreqLabel = (cod) => {
  if (!cod) return ''
  const parts = []
  if (cod.remittanceFrequency) parts.push(cod.remittanceFrequency)
  if (cod.codDPlus != null) parts.push('D+' + cod.codDPlus)
  if (cod.timesPerWeek != null) parts.push(cod.timesPerWeek + '×/week')
  return parts.join(' · ')
}

/* backend parent (light or full) → UI partner row */
export const mapParent = (raw) => {
  const cod = raw.codConfig || {}
  const status = STATUS_LABEL[raw.status] || raw.status || '—'
  return {
    id: raw.id,
    name: raw.name,
    legal: raw.legalEntityName || '',
    verticals: raw.verticals || [],
    integration: INTEGRATION_LABEL[raw.integrationMethod] || raw.integrationMethod || '',
    gst: raw.gstNumber || '',
    website: raw.website || '',
    address: raw.registeredAddress || '',
    logoUrl: raw.logoUrl || '',
    codD2C: cod.slaD2cDays,
    codPTL: cod.slaPtlDays,
    codFreq: codFreqLabel(cod),
    codNotes: cod.notes || '',
    status,
    statusEnum: raw.status,
    statusReason: raw.statusReason || '',
    updated: raw.lastModifiedDate || null,
    createdByUser: raw.createdByUser || '',
    deact:
      raw.status === 'INACTIVE' && raw.statusReason
        ? {by: raw.createdByUser || '—', at: raw.lastModifiedDate, reason: raw.statusReason}
        : null,
    color: colorFor(raw.name),
    pocs: (raw.contacts || []).map(mapContact),
    raw,
  }
}

/* courier_partners doc (camelCase OR snake_case) → UI code row */
export const mapCode = (doc) => {
  const pick = (...keys) => {
    for (const k of keys) if (doc[k] != null) return doc[k]
    return null
  }
  const stp = pick('serviceTypePrimary', 'service_type_primary')
  const sts = pick('serviceTypeSecondary', 'service_type_secondary')
  const serviceTypes = []
  ;[stp].concat(Array.isArray(sts) ? sts : [sts]).forEach((s) => {
    if (s && serviceTypes.indexOf(s) < 0) serviceTypes.push(s)
  })
  const active = pick('isActive', 'is_active', 'active')
  return {
    code: pick('cpAccountCode', 'cp_account_code') || '',
    display: doc.name || pick('cpAccountCode', 'cp_account_code') || '',
    prozoId: pick('prozoId', 'prozo_id') || '',
    /* Courier Code Display (phase 1): derived on the backend; null until the naming attributes exist */
    logicalName: pick('logicalName', 'logical_name') || '',
    priority: (() => { const p = pick('priority'); return p == null || p === '' ? null : Number(p) })(),
    serviceTypes,
    vertical: 'D2C',
    status: active === false || active === 0 || active === '0' ? 'Inactive' : 'Active',
    parent: doc.parent,
    parentId: pick('parentId', 'parent_id'),
    raw: doc,
  }
}

/* timeline summary — signed = SIGNED+ACTIVE, pending = EMAIL_RECEIVED+ACTIVE */
export const timelineSummary = (items) => {
  let signed = 0
  let pending = 0
  ;(items || []).forEach((t) => {
    const a = t.agreement || {}
    if (a.status !== 'ACTIVE') return
    if (a.stage === 'SIGNED') signed++
    else if (a.stage === 'EMAIL_RECEIVED') pending++
  })
  return {signed, pending}
}

/* does a timeline item cover an account code? */
export const itemCoversCode = (item, code) => {
  const a = item.agreement || {}
  return !!a.appliesToAll || (a.linkedCpAccountCodes || []).indexOf(code) >= 0
}

/* agreement-status badge for a code, derived from the server timeline */
export const codeAgreementBadgeFromTimeline = (code, items) => {
  const mine = (items || []).filter((t) => itemCoversCode(t, code))
  const active = mine.filter((t) => (t.agreement || {}).status === 'ACTIVE')
  const signed = active.some((t) => t.agreement.stage === 'SIGNED')
  const pending = active.some((t) => t.agreement.stage === 'EMAIL_RECEIVED')
  if (signed && pending)
    return {
      key: 'addendum',
      tip: 'Signed agreement in force; a newer agreement is email-received and pending signature.',
    }
  if (signed) return {key: 'signed', tip: 'Operating under the current signed agreement.'}
  if (pending)
    return {key: 'approvalpending', tip: 'Only a Stage 1 (email received) agreement exists — signed agreement pending.'}
  return {key: 'norecord', tip: 'No agreement on file for this code.'}
}
